import React, { useEffect, useState } from "react";
import gsap from "gsap";
import AWS from "aws-sdk";
import ReactGA from "react-ga4";
import styled from "styled-components";
import Modal from "../Modal";
import Tick from "../../assets/Tick.svg";
import EmailVideo from "../../assets/Medi_Email_v2_2_recolored.mp4";
import { PrimaryButton, SecondaryButton } from "../Button";
import ArrowRight from "../Button/Icons/ArrowRight";
import { useEmailInfo } from "../../context/emailContext";
import { useOffers } from "../../context/offerContext";
import { useResponses } from "../../context/responseContext";
import { questions } from "../../routes/Questionnaire/questions";
import { MEASUREMENT_LABELS } from "../Measurements";
import { saveEmailToDB } from "../../utils/indexedDB"; // Import IndexedDB utility functions


export default function SendEmailModal({ isOpen, close, reset }) {
  const [isSent, setIsSent] = useState(false);
  const { emailInfo } = useEmailInfo();
  const { selected, selectedProduct } = useOffers();
  const { responses } = useResponses();

  const { email, patientName } = emailInfo;
  const { color, compression_class, limb, measurements, symptoms, toe_type } =
    responses;

  const compressionClass = compression_class
    ? compression_class.length > 0
      ? questions[2].answers.find((e) => e.key === compression_class).heading
      : null
    : null;

  const legShape = limb
    ? questions[1].answers.find((e) => e.key === limb).heading
    : null;

  const symptomHeadings = symptoms
    ? symptoms.map(
        (item) => questions[0].answers.find((e) => e.key === item).heading
      )
    : [];

  const leftLeg = selectedProduct["left"];
  const rightLeg = selectedProduct["right"];

  const getMeasurements = (measurements) => {
    if (!measurements) {
      return "";
    }

    let info = "<br/><br/>";

    const measurementsList = [
      "measurement_BD",
      "CC",
      "CB",
      "measurement_CB1",
      "ID",
    ];

    const leftCount = measurements["left"]
      ? Object.keys(measurements["left"]).length
      : 0;
    const rightCount = measurements["right"]
      ? Object.keys(measurements["right"]).length
      : 0;

    if (measurements) {
      if (measurements["left"] && leftCount > 0) {
        info += "<b>Left leg</b><br/>";

        measurementsList.forEach((key) => {
          if (
            measurements["left"][key] !== null &&
            measurements["left"][key] !== undefined
          ) {
            info += `${MEASUREMENT_LABELS[key]}: ${measurements["left"][key]}cm<br/>`;
          }
        });

        info += "<br/>";
      }

      if (measurements["right"] && rightCount > 0) {
        info += "<b>Right leg</b><br/>";

        measurementsList.forEach((key) => {
          if (
            measurements["right"][key] !== null &&
            measurements["right"][key] !== undefined
          ) {
            info += `${MEASUREMENT_LABELS[key]}: ${measurements["right"][key]}cm<br/>`;
          }
        });

        info += "<br/>";
      }
    }

    return info;
  };

  const legInfoToHtml = (leg, heading) => {
    let info = `<br/><br/><b>${heading}</b><br/>`;

    if (leg) {
      info += `Order code: ${leg["code"]}<br/>`;
      info += `PIP code: ${leg["pip"]}<br/>`;
      info += `Size: ${leg["size"]}<br/>`;
      if (color) {
        info += `Colour: ${color}<br/>`;
      }
      if (toe_type) {
        info += `Toe style: ${toe_type}<br/>`;
      }
    }

    return info;
  };

  const userInput = `<b>Symptoms:</b> ${symptomHeadings.join(
    ", "
  )}<br/><b>Leg shape:</b> ${legShape}<br/><b>Compression class:</b> ${compressionClass}`;

  const measurementsText = `Measurements of patient limb(s) that were recorded: ${getMeasurements(
    measurements
  )}`;

  const leftLegInfo =
    leftLeg === null ? null : legInfoToHtml(leftLeg, "Left Leg");
  const rightLegInfo =
    rightLeg === null ? null : legInfoToHtml(rightLeg, "Right Leg");

  const emailBody = `I have reviewed a patient today to assess their compression requirements and recommend that they are prescribed the following garment: ${
    selected && selected.displayName
  }. Please find all of the relevant information below:<br/><br/>Additional details: ${patientName} <br/><br/>${userInput}<br/><br/>${measurementsText}The recommended product, ${
    selected && selected.displayName
  } was selected, together with the following information:${
    leftLegInfo ? leftLegInfo : ""
  }${rightLegInfo ? rightLegInfo : ""}`;

  
  // Fallback email sending function when form is not available
  const sendEmailDirectly = async () => {
    console.log("📤 [SendEmailModal] sendEmailDirectly: Sending email without form");
    
    // Prepare email data from context and props
    const data = {
      email: email || '',
      name: patientName || '',
      message: emailBody
    };
    
    console.log("📧 [SendEmailModal] sendEmailDirectly: Email data prepared:", data);
    console.log("🔍 [SendEmailModal] sendEmailDirectly: API endpoint:", "https://y0eqv28o73.execute-api.us-east-1.amazonaws.com/PROD/static-site-mailer-dev-staticSiteMailer");
    
    if (navigator.onLine) {
      console.log("✅ [SendEmailModal] sendEmailDirectly: Online - attempting direct send");
      try {
        console.log("🌐 [SendEmailModal] sendEmailDirectly: Starting fetch request...");
        const response = await fetch(
          "https://y0eqv28o73.execute-api.us-east-1.amazonaws.com/PROD/static-site-mailer-dev-staticSiteMailer",
          {
            method: "POST",
            headers: {
              "Accept": "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          }
        );
        
        console.log("🌐 [SendEmailModal] sendEmailDirectly: Fetch completed, response status:", response.status, response.statusText);
        console.log("🌐 [SendEmailModal] sendEmailDirectly: Response headers:", Object.fromEntries(response.headers.entries()));
        
        if (response.ok) {
          const responseText = await response.text();
          console.log("✅ [SendEmailModal] sendEmailDirectly: Email sent successfully! Response:", responseText);
          return true;
        } else {
          const errorText = await response.text();
          console.error("❌ [SendEmailModal] sendEmailDirectly: Failed to send email:", errorText);
          console.error("❌ [SendEmailModal] sendEmailDirectly: Response status:", response.status, response.statusText);
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
      } catch (error) {
        console.error("🚨 [SendEmailModal] sendEmailDirectly: Error sending email:", error);
        console.error("🚨 [SendEmailModal] sendEmailDirectly: Error details:", {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        
        // Save to IndexedDB and register sync
        console.log("💾 [SendEmailModal] sendEmailDirectly: Saving to IndexedDB due to error");
        await saveEmailToDB(data);
        
        if ("serviceWorker" in navigator && "SyncManager" in window) {
          console.log("🔄 [SendEmailModal] sendEmailDirectly: Registering background sync");
          try {
            const registration = await navigator.serviceWorker.ready;
            await registration.sync.register("mediuk-sync");
            console.log("✅ [SendEmailModal] sendEmailDirectly: Background Sync registered");
          } catch (syncError) {
            console.error("❌ [SendEmailModal] sendEmailDirectly: Failed to register sync:", syncError);
          }
        }
        return false;
      }
    } else {
      // Offline: Save email to IndexedDB
      console.log("📴 [SendEmailModal] sendEmailDirectly: Offline - saving to IndexedDB");
      await saveEmailToDB(data);

      if ("serviceWorker" in navigator && "SyncManager" in window) {
        console.log("🔄 [SendEmailModal] sendEmailDirectly: Registering background sync for offline email");
        try {
          const registration = await navigator.serviceWorker.ready;
          await registration.sync.register("mediuk-sync");
          console.log("✅ [SendEmailModal] sendEmailDirectly: Background Sync registered");
        } catch (syncError) {
          console.error("❌ [SendEmailModal] sendEmailDirectly: Failed to register sync:", syncError);
        }
      }
      return true; // Return true for offline as it was queued successfully
    }
  };

  // fade in first question content
  const resetAnimation = () => {
    gsap.fromTo(
      [...document.querySelectorAll("#content")],
      { opacity: 1 },
      {
        opacity: 1,
        duration: 0.5,
        onComplete: () => {
          close();
          setIsSent(false);
          reset();

          // fade back in first question content (will be 0 opacity by default)
          gsap.fromTo(
            [...document.querySelectorAll("#content")],
            { opacity: 0 },
            {
              opacity: 1,
              duration: 0.5,
            }
          );
        },
      }
    );
  };

  useEffect(() => {
    console.log("📋 [SendEmailModal] useEffect: Setting up form submission handler, isOpen:", isOpen);
    
    if (!isOpen) {
      console.log("⏭️ [SendEmailModal] useEffect: Modal not open, skipping form setup");
      return;
    }

    // Try to find the form with a small delay to ensure DOM is ready
    const setupFormHandler = () => {
      const form = document.querySelector('#email-submit-form');

      if (form) {
        console.log("✅ [SendEmailModal] useEffect: Form found:", form);
        
        // Remove any existing event listeners to prevent duplicates
        form.onsubmit = null;
        
        form.onsubmit = async function (e) {
          e.preventDefault();
          console.log("📤 [SendEmailModal] onsubmit: Form submission triggered");
      
          // Prepare data to send
          const data = {};
          const formElements = Array.from(form.elements);
          
          formElements.forEach(input => {
            if (input.name && input.name.trim() !== '') {
              data[input.name] = input.value;
            }
          });
          
          data.message = emailBody;
            
          if (navigator.onLine) {
            
            // Construct an HTTP request
            var xhr = new XMLHttpRequest();
            
           
            
            // Set up the request
            xhr.open(form.method, form.action, true);
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.setRequestHeader('Content-Type', 'application/json');
            
            // Set timeout (30 seconds)
            xhr.timeout = 30000;
            
            
            // Send the request
            try {
              
              xhr.send(JSON.stringify(data));
              
              
              // Add a timeout check to see if the request is hanging
              setTimeout(() => {
                console.log("⏰ [SendEmailModal] onsubmit: 5 second check - XHR ready state:", xhr.readyState, "Status:", xhr.status);
                if (xhr.readyState === 1) {
                  console.warn("⚠️ [SendEmailModal] onsubmit: XHR still in OPENED state after 5 seconds - request may be blocked");
                }
              }, 5000);
              
            } catch (sendError) {
              console.error("💥 [SendEmailModal] onsubmit: Error calling xhr.send():", sendError);
            }
            
            // Callback function
            xhr.onloadend = response => {
             
              if (response.target.status === 0) {
                console.error("🚨 [SendEmailModal] xhr.onloadend: Status 0 - This indicates a network error, CORS issue, or blocked request");
              }
              
              if (response.target.status !== 200) {
                console.error("❌ [SendEmailModal] xhr.onloadend: Error sending email, status:", response.target.status);
                console.error("❌ [SendEmailModal] xhr.onloadend: Response text:", response.target.responseText);
                
                console.log("💾 [SendEmailModal] xhr.onloadend: Saving to IndexedDB due to send failure");
                saveEmailToDB(data); // Save to IndexedDB if sending fails
                
                // Register a background sync task
                if ("serviceWorker" in navigator && "SyncManager" in window) {
                  console.log("🔄 [SendEmailModal] xhr.onloadend: Registering background sync");
                  navigator.serviceWorker.ready.then((registration) => {
                    registration.sync.register("mediuk-sync").then(() => {
                      console.log("✅ [SendEmailModal] xhr.onloadend: Background Sync registered successfully.");
                    }).catch((syncError) => {
                      console.error("❌ [SendEmailModal] xhr.onloadend: Failed to register sync task:", syncError);
                    });
                  });
                } else {
                  console.warn("⚠️ [SendEmailModal] xhr.onloadend: Background sync not available");
                }
              } else {
                console.log("✅ [SendEmailModal] xhr.onloadend: Email sent successfully!");
                console.log("🎉 [SendEmailModal] xhr.onloadend: Success response text:", response.target.responseText);
              }
            };
            
            // Handle network errors
            xhr.onerror = () => {
              console.error("🚨 [SendEmailModal] xhr.onerror: Network error occurred while sending email");
              
              console.log("💾 [SendEmailModal] xhr.onerror: Saving to IndexedDB due to network error");
              saveEmailToDB(data); // Save to IndexedDB if network fails
              
              // Register a background sync task
              if ("serviceWorker" in navigator && "SyncManager" in window) {
                console.log("🔄 [SendEmailModal] xhr.onerror: Registering background sync");
                navigator.serviceWorker.ready.then((registration) => {
                  registration.sync.register("mediuk-sync").then(() => {
                    console.log("✅ [SendEmailModal] xhr.onerror: Background Sync registered successfully.");
                  }).catch((syncError) => {
                    console.error("❌ [SendEmailModal] xhr.onerror: Failed to register sync task:", syncError);
                  });
                });
              } else {
                console.warn("⚠️ [SendEmailModal] xhr.onerror: Background sync not available");
              }
            };
          } else {
            // Offline: Save email to IndexedDB
            console.log("📴 [SendEmailModal] onsubmit: Offline - saving email to IndexedDB");
            await saveEmailToDB(data);
      
            // Register a background sync task
            if ("serviceWorker" in navigator && "SyncManager" in window) {
              console.log("🔄 [SendEmailModal] onsubmit: Registering background sync for offline email");
              navigator.serviceWorker.ready.then((registration) => {
                registration.sync.register("mediuk-sync").then(() => {
                  console.log("✅ [SendEmailModal] onsubmit: Background Sync registered successfully.");
                }).catch((syncError) => {
                  console.error("❌ [SendEmailModal] onsubmit: Failed to register sync task:", syncError);
                });
              });
            } else {
              console.warn("⚠️ [SendEmailModal] onsubmit: Background sync not available");
            }
      
            setIsSent(true);
            console.log("✅ [SendEmailModal] onsubmit: Email queued for offline sending, UI updated");
          }
        };
      } else {
        console.warn("⚠️ [SendEmailModal] setupFormHandler: Form #email-submit-form not found in DOM");
        console.log("🔍 [SendEmailModal] setupFormHandler: Available forms:", Array.from(document.querySelectorAll('form')).map(f => f.id || 'no-id'));
        
        // If form is not found after several attempts, we'll handle submission through the modal button
        console.log("💡 [SendEmailModal] setupFormHandler: Will handle email sending through modal button instead");
      }
    };

    // Initial setup
    setupFormHandler();

    // Cleanup function
    return () => {
      const form = document.querySelector('#email-submit-form');
      if (form) {
        form.onsubmit = null;
        console.log("🧹 [SendEmailModal] useEffect cleanup: Removed form event listener");
      }
    };
  }, [isOpen, emailBody]);

  return (
    <Modal
      id="content"
      isOpen={isOpen}
      customPadding={true}
      close={() => {
        close();
        setIsSent(false);
      }}
    >
      {isSent && <img alt="" src={Tick} />}
      <h3 style={{ fontSize: "18px", lineHeight: "28px" }}>{isSent ? "Your email has been sent" : "Send the email"}</h3>
      {!isSent && (
        <video
          autoPlay
          muted
          playsInline
          webkit-playsinline="true"
          preload="metadata"
          style={{ width: "170px" }}
        >
          <source src={EmailVideo} type="video/mp4" />
        </video>
      )}
      {isSent ? (
        <p style={{ fontSize: "16px", lineHeight: "26px" }}>
          Thank you.
        </p>
      ) : (
        <>
          <p style={{ fontSize: "16px", lineHeight: "26px", marginBottom: "32px" }}>
            Are you sure you have all the information correct before sending? This action cannot be undone.
          </p>
        </>
      )}
      <ButtonContainer $isSent={isSent}>
        {!isSent && (
          <SecondaryButton onClick={() => close()}>Cancel</SecondaryButton>
        )}
        <PrimaryButton
          type="submit"
          form="email-submit-form"
          onClick={async (e) => {
          console.log("🔘 [SendEmailModal] PrimaryButton onClick: Button clicked, isSent:", isSent);
          
          if (isSent) {
            // close modal and return to start
            close();
            resetAnimation();
            return;
          }

          ReactGA.event({
            category: "User",
            action: `Sent email`,
          });

          // Check if form exists for submission
          const form = document.querySelector('#email-submit-form');
          console.log("🔍 [SendEmailModal] PrimaryButton onClick: Form lookup result:", form);
          
          if (!form) {
            console.log("⚠️ [SendEmailModal] PrimaryButton onClick: Form not found, using direct sending");
            // Prevent any default button behavior
            e.preventDefault();
            
            try {
              const success = await sendEmailDirectly();
              if (success) {
                setIsSent(true);
                console.log("✅ [SendEmailModal] PrimaryButton onClick: Direct email sending completed");
              } else {
                console.error("❌ [SendEmailModal] PrimaryButton onClick: Direct email sending failed");
                // Still set as sent since it was queued
                setIsSent(true);
              }
            } catch (error) {
              console.error("💥 [SendEmailModal] PrimaryButton onClick: Error in direct sending:", error);
              // Still set as sent since it was likely queued
              setIsSent(true);
            }
          } else {
            console.log("✅ [SendEmailModal] PrimaryButton onClick: Form found, triggering form submission");
            
            // Manually trigger form submission to ensure it happens
            try {
              console.log("🔄 [SendEmailModal] PrimaryButton onClick: Manually dispatching submit event");
              const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
              const submitted = form.dispatchEvent(submitEvent);
              console.log("📤 [SendEmailModal] PrimaryButton onClick: Submit event dispatched, result:", submitted);
            } catch (dispatchError) {
              console.error("❌ [SendEmailModal] PrimaryButton onClick: Error dispatching submit event:", dispatchError);
            }
            
            setIsSent(true);
            // Form submission will be handled by the form's onsubmit handler
          }
        }}
      >
        {isSent ? "Continue" : "Send email"}
        {isSent && <ArrowRight />}
      </PrimaryButton>
      </ButtonContainer>
    </Modal>
  );
}

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: ${props => props.$isSent ? 'center' : 'space-between'};
  align-items: center;
  
  button {
    margin-bottom: 0 !important;
  }
`;